import axios from 'axios'
import type { AxiosError, InternalAxiosRequestConfig } from 'axios'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

export const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // needed for httpOnly refresh-token cookie
  headers: { 'Content-Type': 'application/json' },
})

// Attach access token from store to each request
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Token injected by auth store via setAuthHeader()
  return config
})

// 401 → attempt silent token refresh, then retry once
let isRefreshing = false
let refreshQueue: Array<(token: string) => void> = []

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean }

    if (error.response?.status !== 401 || original._retried) {
      return Promise.reject(error)
    }

    // Skip refresh for auth endpoints to avoid infinite loop
    if (original.url?.includes('/auth/')) {
      return Promise.reject(error)
    }

    if (isRefreshing) {
      return new Promise((resolve) => {
        refreshQueue.push((token) => {
          original.headers['Authorization'] = `Bearer ${token}`
          resolve(apiClient(original))
        })
      })
    }

    isRefreshing = true
    original._retried = true

    try {
      const { data } = await apiClient.post<{ accessToken: string }>('/v1/auth/refresh')
      const newToken = data.accessToken

      setAuthHeader(newToken)
      refreshQueue.forEach((cb) => cb(newToken))
      refreshQueue = []

      original.headers['Authorization'] = `Bearer ${newToken}`
      return apiClient(original)
    } catch {
      refreshQueue = []
      // Let auth store react to 401 via router
      return Promise.reject(error)
    } finally {
      isRefreshing = false
    }
  }
)

export function setAuthHeader(token: string | null) {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
  } else {
    delete apiClient.defaults.headers.common['Authorization']
  }
}
